import { Injectable } from "@angular/core";
import type { PendoProfile } from "../../pendo-profile.model";

export const PENDO_IDS_CACHE_KEY = "firm360.observability.pendo.profile";

interface CachedPendoIds {
    fingerprint: string;
    ids: PendoProfile.Ids;
}

/**
 * Reads and writes {@link PendoProfile.Ids} to/from local storage
 */
@Injectable()
export class PendoIdsLocalStorageCacheService {
    read(token: string): PendoProfile.Ids | undefined {
        try {
            const raw = localStorage.getItem(PENDO_IDS_CACHE_KEY);
            if (!raw) return undefined;
            const entry: unknown = JSON.parse(raw);
            return isCachedIds(entry) &&
                entry.fingerprint === getTokenFingerprint(token)
                ? entry.ids
                : undefined;
        } catch {
            return undefined;
        }
    }

    write(token: string, ids: PendoProfile.Ids): void {
        const entry: CachedPendoIds = {
            fingerprint: getTokenFingerprint(token),
            ids,
        };
        try {
            localStorage.setItem(PENDO_IDS_CACHE_KEY, JSON.stringify(entry));
        } catch {
            // Best-effort; the next profile change tries again.
            console.warn("Failed to cache Pendo IDs in local storage:", ids);
        }
    }
}

/**
 * A stable, non-reversible stand-in for the session token: the same token
 * always means the same user in the same firm, so an entry stored under it is
 * safe to trust on reload without asking the server who this is. Not
 * cryptographic — it only needs to keep the token itself out of the entry.
 */
function getTokenFingerprint(token: string): string {
    // cyrb53
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;
    for (let i = 0; i < token.length; i++) {
        const ch = token.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

function isCachedIds(value: unknown): value is CachedPendoIds {
    if (typeof value !== "object" || value === null) return false;
    const entry = value as Partial<CachedPendoIds>;
    return (
        typeof entry.fingerprint === "string" &&
        typeof entry.ids?.visitorId === "string" &&
        typeof entry.ids.accountId === "string"
    );
}
